import React, { useState, useEffect, useRef } from 'react';
import { connectedUserAPI, planAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './ConnectedUsers.css';

const AUTO_APPLY_SECONDS = 30;

const ConnectedUsers = () => {
    const { selectedBranch } = useBranchStore();
    const [sessions, setSessions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeTarget, setUpgradeTarget] = useState(null);
    const [upgradeForm, setUpgradeForm] = useState({ newPlanId: '', extendMinutes: '', reason: '', forceReconnect: false });
    const [bulkPlanId, setBulkPlanId] = useState('');
    const [countdown, setCountdown] = useState(AUTO_APPLY_SECONDS);
    const countdownRef = useRef(null);

    useEffect(() => {
        fetchAll();
        const interval = setInterval(fetchAll, 30000); // light auto-refresh for a "live" feel
        return () => clearInterval(interval);
    }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [sessRes, planRes] = await Promise.all([
                connectedUserAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId),
            ]);
            setSessions(sessRes.data.data || []);
            setPlans(planRes.data.data.plans || []);
        } catch (error) {
            toast.error('Failed to fetch connected users');
        } finally {
            setLoading(false);
        }
    };

    const filtered = sessions.filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return s.userId?.name?.toLowerCase().includes(q) || s.userId?.mobile?.includes(q) || s.deviceMac?.toLowerCase().includes(q);
    });

    const toggleSelect = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        setSelected(selected.length === filtered.length ? [] : filtered.map(s => s._id));
    };

    const clearCountdown = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    };

    const openUpgrade = (session) => {
        setUpgradeTarget(session);
        setUpgradeForm({ newPlanId: session.userId?.planId?._id || '', extendMinutes: '', reason: '', forceReconnect: false });
        setCountdown(AUTO_APPLY_SECONDS);
        setShowUpgradeModal(true);
    };

    const closeUpgradeModal = () => {
        clearCountdown();
        setShowUpgradeModal(false);
    };

    // Start countdown when modal opens
    useEffect(() => {
        if (!showUpgradeModal) return;
        clearCountdown();
        setCountdown(AUTO_APPLY_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearCountdown();
    }, [showUpgradeModal]);

    // Auto-apply when countdown hits 0
    useEffect(() => {
        if (showUpgradeModal && countdown === 0) {
            handleUpgrade();
        }
    }, [countdown]);

    const handleUpgrade = async () => {
        clearCountdown();
        try {
            const res = await connectedUserAPI.upgrade(upgradeTarget._id, upgradeForm);
            toast.success(res.data.message || 'Plan applied & pushed to MikroTik via CoA');
            setShowUpgradeModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to upgrade plan');
        }
    };

    const handleBulk = async (action) => {
        if (selected.length === 0) return toast.warning('Select at least one user');
        if (action === 'assign_plan' && !bulkPlanId) return toast.warning('Select a plan first');
        if (!window.confirm(`${action} for ${selected.length} user(s)?`)) return;
        try {
            const res = await connectedUserAPI.bulkAction({ sessionIds: selected, action, planId: bulkPlanId });
            toast.success(res.data.message);
            setSelected([]);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Bulk action failed');
        }
    };

    const formatDuration = (startTime) => {
        const mins = Math.floor((Date.now() - new Date(startTime).getTime()) / 60000);
        if (mins < 60) return `${mins}m`;
        return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-wifi"></i>
                            Connected Users
                        </h1>
                        <p className="page-subtitle-modern">Live plan upgrades — push new bandwidth instantly via CoA without disconnecting</p>
                    </div>
                </div>
            </div>

            <div className="connected-toolbar">
                <input
                    type="text"
                    className="connected-search"
                    placeholder="Search by name, mobile, or MAC..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {selected.length > 0 && (
                    <div className="bulk-actions-bar">
                        <span>{selected.length} selected</span>
                        <select value={bulkPlanId} onChange={e => setBulkPlanId(e.target.value)}>
                            <option value="">Assign plan...</option>
                            {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                        <button className="btn-secondary-modern" onClick={() => handleBulk('assign_plan')}>Assign</button>
                        <button className="btn-secondary-modern" onClick={() => handleBulk('extend')}>Extend +60m</button>
                        <button className="btn-secondary-modern" onClick={() => handleBulk('disable')}>Disable</button>
                        <button className="btn-danger-modern" onClick={() => handleBulk('disconnect')}>Disconnect</button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading connected users...</p></div>
            ) : filtered.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-wifi"></i></div>
                    <h3>No Connected Users</h3>
                    <p>Active sessions will appear here in real time.</p>
                </div>
            ) : (
                <div className="table-wrapper-modern">
                    <table className="table-modern">
                        <thead>
                            <tr>
                                <th><input type="checkbox" checked={selected.length === filtered.length} onChange={toggleSelectAll} /></th>
                                <th>User</th>
                                <th>Current Plan</th>
                                <th>MAC</th>
                                <th>IP</th>
                                <th>Duration</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(session => (
                                <tr key={session._id}>
                                    <td><input type="checkbox" checked={selected.includes(session._id)} onChange={() => toggleSelect(session._id)} /></td>
                                    <td>
                                        <div className="user-cell">
                                            <strong>{session.userId?.name || 'Guest'}</strong>
                                            <span>{session.userId?.mobile}</span>
                                        </div>
                                    </td>
                                    <td>{session.userId?.planId?.name || '—'}</td>
                                    <td><code>{session.deviceMac}</code></td>
                                    <td>{session.framedIpAddress}</td>
                                    <td>{formatDuration(session.startTime)}</td>
                                    <td>
                                        <button className="btn-primary-modern btn-sm" onClick={() => openUpgrade(session)}>
                                            <i className="fas fa-arrow-up"></i> Upgrade
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showUpgradeModal && (
                <div className="modal-overlay-modern" onClick={closeUpgradeModal}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Upgrade Plan — {upgradeTarget?.userId?.name || 'Guest'}</h2>
                            <button className="modal-close" onClick={closeUpgradeModal}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="form-grid">
                                <div className="form-group-modern full-width">
                                    <label>New Plan</label>
                                    <select value={upgradeForm.newPlanId} onChange={e => setUpgradeForm({ ...upgradeForm, newPlanId: e.target.value })}>
                                        <option value="">Keep current plan</option>
                                        {plans.map(p => <option key={p._id} value={p._id}>{p.name} ({p.bandwidth.downloadSpeed}/{p.bandwidth.uploadSpeed} Kbps)</option>)}
                                    </select>
                                    <p className="input-hint">Applied live via CoA to MikroTik — no disconnect needed, unless "Force Reconnect" is checked.</p>
                                </div>
                                <div className="form-group-modern">
                                    <label>Extend Validity (minutes)</label>
                                    <input type="number" value={upgradeForm.extendMinutes} onChange={e => setUpgradeForm({ ...upgradeForm, extendMinutes: e.target.value })} placeholder="Optional" />
                                </div>
                                <div className="form-group-modern">
                                    <label>Reason</label>
                                    <input type="text" value={upgradeForm.reason} onChange={e => setUpgradeForm({ ...upgradeForm, reason: e.target.value })} placeholder="Optional, for audit trail" />
                                </div>
                                <div className="form-group-modern full-width">
                                    <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={upgradeForm.forceReconnect} onChange={e => setUpgradeForm({ ...upgradeForm, forceReconnect: e.target.checked })} />
                                        <span>Force reconnect (use if the new plan needs a fresh RADIUS auth, e.g. VLAN change)</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <div className="auto-apply-countdown">
                                <div className="auto-apply-bar" style={{ width: `${(countdown / AUTO_APPLY_SECONDS) * 100}%` }} />
                                <span>Auto-applying in {countdown}s…</span>
                            </div>
                            <button className="btn-secondary-modern" onClick={closeUpgradeModal}>Cancel</button>
                            <button className="btn-primary-modern" onClick={handleUpgrade}><i className="fas fa-check"></i> Apply Now</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConnectedUsers;
